import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from '../axiosInterceptor';

export interface InviteStudentPayload {
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  focus_area: string;
  notes: string;
}

export interface Student {
  id: string;
  name?: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  focus_area: string;
  notes: string;
  status: string;
  completed_sessions: number;
  last_session: string | null;
  created_at: string;
  role?: string;
  coach_id?: string;
  assigned_coach?: {
    id: string;
    name: string;
    avatar?: string;
    area_of_expertise?: string;
  };
}

export interface InviteStudentResponse {
  type: "new_user" | "existing_user";
  user_id: string;
  email: string;
  role?: string;
  status: string;
  invitation_token?: string;
  expires_at?: string;
  temporary_password?: string;
  message: string;
}

interface StudentState {
  students: Student[];
  loading: boolean;
  error: string | null;
  inviteSuccess: boolean;
  lastInviteResponse: InviteStudentResponse | null;
}

const initialState: StudentState = {
  students: [],
  loading: false,
  error: null,
  inviteSuccess: false,
  lastInviteResponse: null,
};

// Async thunk for inviting a student
export const inviteStudent = createAsyncThunk(
  'student/invite',
  async (payload: InviteStudentPayload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post('/api/admin/student/invite', payload);
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to invite student');
    }
  }
);

// Async thunk for fetching all students
export const fetchStudents = createAsyncThunk(
  'student/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get('/api/students');
      return data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch students');
    }
  }
);

const studentSlice = createSlice({
  name: 'student',
  initialState,
  reducers: {
    resetInviteSuccess: (state) => {
      state.inviteSuccess = false;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetStudent: (state) => {
      state.loading = false;
      state.students = [];
      state.error = null;
      state.inviteSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Invite student
      .addCase(inviteStudent.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.inviteSuccess = false;
        state.lastInviteResponse = null;
      })
      .addCase(inviteStudent.fulfilled, (state, action: PayloadAction<InviteStudentResponse>) => {
        state.loading = false;
        state.inviteSuccess = true;
        state.lastInviteResponse = action.payload;
      })
      .addCase(inviteStudent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.inviteSuccess = false;
        state.lastInviteResponse = null;
      })
      // Fetch students
      .addCase(fetchStudents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action: PayloadAction<Student[]>) => {
        state.loading = false;
        state.students = action.payload;
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { resetInviteSuccess, clearError, resetStudent } = studentSlice.actions;
export default studentSlice.reducer;
